/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Safe Multi-Tenant Workspace Switch Pipeline (Task 10.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type { QueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';

/**
 * Executes a zero-data-leak tenant switch:
 * 1. Updates activeWorkspaceId in Zustand store and persists to localStorage
 * 2. Invalidates and clears all cached queries in TanStack Query to prevent cross-tenant data leakage
 * 3. Dispatches global window event 'sophia:workspace-changed'
 */
export function switchWorkspace(workspaceId: string, queryClient?: QueryClient | null): void {
  const { workspaces, activeWorkspaceId: currentId } = useWorkspaceStore.getState();
  if (currentId === workspaceId) {
    return;
  }

  // Validate that workspaceId exists in loaded workspaces
  const targetWs = workspaces.find((w) => w.id === workspaceId);
  if (!targetWs) {
    return;
  }

  // 1. Update Zustand store
  useWorkspaceStore.getState().setActiveWorkspaceId(workspaceId);

  // 2. Clear server cache to prevent cross-tenant data leakage
  if (queryClient) {
    queryClient.clear();
  }

  // 3. Dispatch browser event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('sophia:workspace-changed', {
        detail: {
          previousWorkspaceId: currentId,
          workspaceId,
          workspace: targetWs,
        },
      }),
    );
  }
}
